import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createRoute, Navigate } from '@tanstack/react-router'
import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Folder,
  GanttChart,
  Plus,
  Trash2,
} from 'lucide-react'
import {
  type PointerEvent as ReactPointerEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  MobileBottomSlots,
  MobileTopCenter,
  useMobilePageTitle,
} from '../components/shell/mobile-shell-outlets'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { BarAccentPlus, BarSlot, SheetRow } from '../components/ui/mobile-action-bar'
import { PageHeader } from '../components/ui/page-header'
import { Select } from '../components/ui/select'
import { SidePanel } from '../components/ui/side-panel'
import { SegmentedControl } from '../components/ui/tab-nav'
import { api, type DispositionEntry } from '../lib/api'
import { notify } from '../lib/notify'
import { periodTitle } from '../lib/period-title'
import { cn } from '../lib/utils'
import { Route as rootRoute } from './__root'

export const Route = createRoute({
  getParentRoute: () => rootRoute,
  path: '/disposition',
  component: DispositionPage,
})

function startOfWeek(d: Date): Date {
  const day = d.getDay()
  const diff = d.getDate() - day + (day === 0 ? -6 : 1)
  const start = new Date(d)
  start.setDate(diff)
  start.setHours(0, 0, 0, 0)
  return start
}

function addDays(d: Date, n: number): Date {
  const r = new Date(d)
  r.setDate(r.getDate() + n)
  return r
}

function formatDateISO(d: Date): string {
  return d.toISOString().slice(0, 10)
}

type ViewRange = 'week' | 'month'

interface GanttDragState {
  entryId: string
  /** Index into days[] where the drag started */
  origDayIndex: number
  /** Current day index the pointer is over */
  currentDayIndex: number
  /** Delta in days from original position */
  daysDelta: number
}

const CLICK_THRESHOLD = 4

function DispositionPage() {
  const qc = useQueryClient()
  const { data: me, isLoading: meLoading } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: () => api.me(),
  })

  const [cursor, setCursor] = useState(() => startOfWeek(new Date()))
  const [viewRange, setViewRange] = useState<ViewRange>('week')
  const [createOpen, setCreateOpen] = useState(false)
  const [editEntry, setEditEntry] = useState<DispositionEntry | null>(null)
  const [filterProject, setFilterProject] = useState('')
  const [filterUser] = useState('')
  // Mobile-Shell (<640): Projekt-Filter-Sheet hinterm Bottom-Slot,
  // ◀ Zeitraum ▶ in der Top-Mitte (Muster team-planning).
  const [projectSheet, setProjectSheet] = useState(false)
  useMobilePageTitle('Disposition')

  // ── Gantt drag state ──────────────────────────────────────────────
  const [dragState, setDragState] = useState<GanttDragState | null>(null)
  const dragRef = useRef<GanttDragState | null>(null)
  const longPressTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const touchStartPos = useRef<{ x: number; y: number } | null>(null)
  const pointerStartX = useRef(0)
  const dayColumnRefs = useRef<(HTMLDivElement | null)[]>([])

  const setDrag = useCallback((d: GanttDragState | null) => {
    dragRef.current = d
    setDragState(d)
  }, [])

  const clearLongPress = useCallback(() => {
    if (longPressTimer.current) {
      clearTimeout(longPressTimer.current)
      longPressTimer.current = null
    }
  }, [])

  const days = useMemo(() => {
    const count = viewRange === 'week' ? 7 : 28
    return Array.from({ length: count }, (_, i) => addDays(cursor, i))
  }, [cursor, viewRange])

  const from = formatDateISO(days[0])
  const to = formatDateISO(days[days.length - 1])

  const { data: timelineData, isLoading } = useQuery({
    queryKey: ['disposition', 'timeline', from, to],
    queryFn: () => api.getDispositionTimeline(from, to),
    enabled: !!me?.user && me.user.role === 'admin',
  })

  const { data: usersData } = useQuery({
    queryKey: ['users'],
    queryFn: () => api.listUsers(),
    enabled: !!me?.user,
  })

  const { data: projectsData } = useQuery({
    queryKey: ['projects', { archived: false }],
    queryFn: () => api.listProjects(),
    enabled: !!me?.user,
  })

  const { data: inventoryData } = useQuery({
    queryKey: ['inventory', 'items', '', '', ''],
    queryFn: () => api.listInventory(),
    enabled: !!me?.user,
  })

  const entries = timelineData?.entries ?? []
  const userEntries = timelineData?.userEntries ?? {}
  const inventoryEntries = timelineData?.inventoryEntries ?? {}
  const users = usersData?.users ?? []
  const projects = projectsData?.projects ?? []
  const inventoryItems = inventoryData?.items ?? []

  // Build unique user rows and inventory rows from entries
  const userRows = useMemo(() => {
    const userIds = new Set<string>()
    for (const e of entries) {
      if (e.userId) userIds.add(e.userId)
    }
    return users.filter((u) => userIds.has(u.id) || filterUser === u.id)
  }, [entries, users, filterUser])

  const inventoryRows = useMemo(() => {
    const itemIds = new Set<string>()
    for (const e of entries) {
      if (e.inventoryItemId) itemIds.add(e.inventoryItemId)
    }
    return inventoryItems.filter((i) => itemIds.has(i.id))
  }, [entries, inventoryItems])

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.deleteDisposition(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['disposition'] })
      notify.success('Eintrag gelöscht')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  const moveMut = useMutation({
    mutationFn: ({ id, startTime, endTime }: { id: string; startTime: string; endTime: string }) =>
      api.updateDisposition(id, { startTime, endTime }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['disposition'] })
      notify.success('Verschoben')
    },
    onError: (err: Error) => notify.error(err.message),
  })

  // Find which day column the pointer is over
  const dayIndexUnderX = useCallback((clientX: number): number | null => {
    for (let i = 0; i < dayColumnRefs.current.length; i++) {
      const el = dayColumnRefs.current[i]
      if (!el) continue
      const r = el.getBoundingClientRect()
      if (clientX >= r.left && clientX <= r.right) return i
    }
    return null
  }, [])

  // Commit the drag: shift entry by daysDelta
  const commitDrag = useCallback(
    (state: GanttDragState) => {
      if (state.daysDelta === 0) return
      const allEntries = [
        ...Object.values(userEntries).flat(),
        ...Object.values(inventoryEntries).flat(),
      ]
      const entry = allEntries.find((e) => e.id === state.entryId)
      if (!entry) return
      const newStart = new Date(entry.startTime)
      newStart.setDate(newStart.getDate() + state.daysDelta)
      const newEnd = new Date(entry.endTime)
      newEnd.setDate(newEnd.getDate() + state.daysDelta)
      moveMut.mutate({
        id: entry.id,
        startTime: newStart.toISOString(),
        endTime: newEnd.toISOString(),
      })
    },
    [userEntries, inventoryEntries, moveMut],
  )

  // Start drag for a specific entry
  const startDrag = useCallback(
    (entryId: string, dayIndex: number, clientX: number) => {
      pointerStartX.current = clientX
      setDrag({
        entryId,
        origDayIndex: dayIndex,
        currentDayIndex: dayIndex,
        daysDelta: 0,
      })
    },
    [setDrag],
  )

  // Pointer down on a Gantt bar
  const onBarPointerDown = useCallback(
    (e: ReactPointerEvent, entry: DispositionEntry, dayIndex: number) => {
      e.stopPropagation()
      if (e.button !== 0) return

      if (e.pointerType === 'touch') {
        // Touch: 400ms long-press to enter drag mode
        touchStartPos.current = { x: e.clientX, y: e.clientY }
        clearLongPress()
        longPressTimer.current = setTimeout(() => {
          if (touchStartPos.current) {
            startDrag(entry.id, dayIndex, touchStartPos.current.x)
          }
        }, 400)

        const handleTouchEnd = () => {
          const wasDragging = !!dragRef.current
          clearLongPress()
          if (!wasDragging) {
            // Short tap: open edit panel
            setEditEntry(entry)
          }
          document.removeEventListener('pointerup', handleTouchEnd)
          document.removeEventListener('pointermove', handleTouchMoveCancel)
        }

        const handleTouchMoveCancel = (ev: PointerEvent) => {
          if (!touchStartPos.current) return
          const dx = ev.clientX - touchStartPos.current.x
          const dy = ev.clientY - touchStartPos.current.y
          // If finger moved > 10px before long-press fires, cancel (let scroll work)
          if (Math.abs(dx) > 10 || Math.abs(dy) > 10) {
            clearLongPress()
            document.removeEventListener('pointerup', handleTouchEnd)
            document.removeEventListener('pointermove', handleTouchMoveCancel)
          }
        }

        document.addEventListener('pointerup', handleTouchEnd)
        document.addEventListener('pointermove', handleTouchMoveCancel)
        return
      }

      // Mouse/pen: start drag immediately (click resolved on pointerup via threshold)
      pointerStartX.current = e.clientX
      setDrag({
        entryId: entry.id,
        origDayIndex: dayIndex,
        currentDayIndex: dayIndex,
        daysDelta: 0,
      })
    },
    [clearLongPress, startDrag, setDrag],
  )

  // Document-level pointermove + pointerup while drag is active
  const dragActive = dragState !== null
  // biome-ignore lint/correctness/useExhaustiveDependencies: ref-based access by design
  useEffect(() => {
    if (!dragActive) return
    const onMove = (e: PointerEvent) => {
      const cur = dragRef.current
      if (!cur) return
      const idx = dayIndexUnderX(e.clientX)
      if (idx == null) return
      const delta = idx - cur.origDayIndex
      if (delta !== cur.daysDelta || idx !== cur.currentDayIndex) {
        setDrag({ ...cur, currentDayIndex: idx, daysDelta: delta })
      }
    }
    const onUp = (e: PointerEvent) => {
      const cur = dragRef.current
      if (!cur) {
        setDrag(null)
        return
      }
      // Mouse: if pointer barely moved, treat as click (open edit)
      const totalMove = Math.abs(e.clientX - pointerStartX.current)
      if (totalMove < CLICK_THRESHOLD && cur.daysDelta === 0) {
        const allEntries = [
          ...Object.values(userEntries).flat(),
          ...Object.values(inventoryEntries).flat(),
        ]
        const entry = allEntries.find((en) => en.id === cur.entryId)
        if (entry) setEditEntry(entry)
      } else {
        commitDrag(cur)
      }
      setDrag(null)
      clearLongPress()
    }
    document.addEventListener('pointermove', onMove)
    document.addEventListener('pointerup', onUp)
    return () => {
      document.removeEventListener('pointermove', onMove)
      document.removeEventListener('pointerup', onUp)
    }
  }, [dragActive])

  if (meLoading) return null
  if (!me?.user) return <Navigate to="/login" />
  if (me.user.role !== 'admin') return <Navigate to="/" />

  const onPrev = () => setCursor(addDays(cursor, viewRange === 'week' ? -7 : -28))
  const onNext = () => setCursor(addDays(cursor, viewRange === 'week' ? 7 : 28))
  const onToday = () => setCursor(startOfWeek(new Date()))
  // Kanonisches Format (lib/period-title): „KW 28 · 06.–12. Juli 2026";
  // die 4-Wochen-Ansicht wird zur KW-Spanne („KW 28–31 · …") — identisch
  // zu Kalender-Woche/Team-Planung.
  const rangeTitle = periodTitle(days[0], days[days.length - 1])

  return (
    <section className="animate-fade-in space-y-4">
      {/* Mobile-Shell (<640): ◀ Zeitraum ▶ in der Top-Mitte — Tap auf den
          Titel springt zu Heute (Muster team-planning). */}
      <MobileTopCenter>
        <button
          type="button"
          onClick={onPrev}
          aria-label="Vorheriger Zeitraum"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-text-secondary hover:bg-bg-overlay hover:text-text-primary"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>
        <button
          type="button"
          onClick={onToday}
          aria-label={`Zeitraum: ${rangeTitle} — zu Heute springen`}
          // Inline minWidth:0 schlägt die globale .no-min-tap-Regel, sonst
          // sprengt der lange Zeitraumtitel die Leiste (Gotcha der alten
          // Kalender-Leiste); h-11 hält die 44px-Tap-Höhe.
          style={{ minWidth: 0 }}
          className="no-min-tap flex h-11 min-w-0 flex-1 items-center justify-center rounded-md px-1 text-text-primary hover:bg-bg-overlay"
        >
          <span className="truncate text-xs font-medium">{rangeTitle}</span>
        </button>
        <button
          type="button"
          onClick={onNext}
          aria-label="Nächster Zeitraum"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-text-secondary hover:bg-bg-overlay hover:text-text-primary"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </MobileTopCenter>

      {/* Bottom-Slots (Phone + Tablet-Band): Projekt-Filter → Sheet + ＋ Eintrag — bestehende
          Handler (Mapping §3); der Gantt bleibt eigener overflow-x-Scroller. */}
      <MobileBottomSlots>
        <BarSlot
          label="Projekt"
          icon={<Folder className="h-5 w-5" />}
          active={filterProject !== ''}
          ariaLabel="Nach Projekt filtern"
          ariaHasPopup
          onClick={() => setProjectSheet(true)}
        />
        <BarAccentPlus ariaLabel="Neuer Eintrag" onClick={() => setCreateOpen(true)} />
      </MobileBottomSlots>

      {/* Projekt-Filter-Sheet (<640): bindet dasselbe filterProject wie das
          Toolbar-Select. */}
      <SidePanel open={projectSheet} onClose={() => setProjectSheet(false)} title="Projekt-Filter">
        <div className="flex flex-col gap-1">
          <SheetRow
            active={filterProject === ''}
            label="Alle Projekte"
            onClick={() => setFilterProject('')}
          />
          {projects.map((p) => (
            <SheetRow
              key={p.id}
              active={filterProject === p.id}
              label={p.name}
              onClick={() => setFilterProject(p.id)}
            />
          ))}
        </div>
      </SidePanel>

      {/* In-Content-Kopf: <640 übernimmt die Shell (Titel oben, Aktionen
          unten) — phone:hidden (Basis sichtbar, deckt Hoch- + Querformat ab). */}
      <PageHeader
        title="Disposition"
        actions={
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4" /> Eintrag
          </Button>
        }
      />

      {/* Controls — ◀▶/Heute/Zeitraum + Projekt-Select übernimmt <640 die
          Shell (Top-Mitte bzw. Sheet); der Woche/Monat-Toggle bleibt auch
          mobil im Content. */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex phone:hidden items-center gap-2">
          <Button variant="ghost" size="icon" onClick={onPrev}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="secondary" size="sm" onClick={onToday}>
            Heute
          </Button>
          <Button variant="ghost" size="icon" onClick={onNext}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <span className="text-sm font-medium text-text-primary ml-2">{rangeTitle}</span>
        </div>
        <span className="flex-1" />
        <SegmentedControl
          value={viewRange}
          onChange={setViewRange}
          options={[
            { id: 'week', label: 'Woche' },
            { id: 'month', label: 'Monat' },
          ]}
        />
        <div className="phone:hidden">
          <Select
            value={filterProject}
            onChange={(e) => setFilterProject(e.target.value)}
            className="w-36 h-8 text-xs"
          >
            <option value="">Alle Projekte</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {/* Gantt Timeline */}
      {isLoading ? (
        <p className="text-sm text-text-tertiary py-8 text-center">Lade Disposition …</p>
      ) : entries.length === 0 && userRows.length === 0 && inventoryRows.length === 0 ? (
        <div className="py-16 text-center space-y-3">
          <GanttChart className="h-12 w-12 mx-auto text-text-tertiary/40" strokeWidth={1.5} />
          <p className="text-sm text-text-tertiary">Keine Einträge in diesem Zeitraum.</p>
        </div>
      ) : (
        <div
          className="overflow-x-auto border border-border-subtle rounded-xl"
          style={{ touchAction: dragState ? 'none' : 'auto' }}
        >
          <div
            className="min-w-max"
            style={{
              display: 'grid',
              gridTemplateColumns: `180px repeat(${days.length}, minmax(40px, 1fr))`,
            }}
          >
            {/* Header row */}
            <div className="sticky left-0 z-10 bg-bg-elevated border-b border-r border-border-subtle p-2 text-xs font-medium text-text-secondary">
              Ressource
            </div>
            {days.map((d, dayIndex) => {
              const isToday = formatDateISO(d) === formatDateISO(new Date())
              const isWeekend = d.getDay() === 0 || d.getDay() === 6
              return (
                <div
                  key={d.toISOString()}
                  ref={(el) => {
                    dayColumnRefs.current[dayIndex] = el
                  }}
                  className={cn(
                    'p-1 text-center text-2xs border-b border-border-subtle',
                    isToday && 'bg-accent/10',
                    isWeekend && 'bg-bg-overlay/30',
                  )}
                >
                  <div className="text-text-tertiary">
                    {['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'][d.getDay()]}
                  </div>
                  <div
                    className={cn('font-medium', isToday ? 'text-accent' : 'text-text-secondary')}
                  >
                    {d.getDate()}
                  </div>
                </div>
              )
            })}

            {/* Section: Mitarbeiter */}
            {userRows.length > 0 && (
              <div
                className="sticky left-0 z-10 bg-bg-elevated/80 border-b border-r border-border-subtle p-2 text-2xs font-bold text-text-tertiary uppercase tracking-wider"
                style={{ gridColumn: `1 / -1` }}
              >
                Mitarbeiter
              </div>
            )}
            {userRows.map((user) => (
              <GanttRow
                key={`u-${user.id}`}
                label={user.name}
                days={days}
                entries={(userEntries[user.id] ?? []).filter(
                  (e) => !filterProject || e.projectId === filterProject,
                )}
                dragState={dragState}
                onBarPointerDown={onBarPointerDown}
              />
            ))}

            {/* Section: Inventar */}
            {inventoryRows.length > 0 && (
              <div
                className="sticky left-0 z-10 bg-bg-elevated/80 border-b border-r border-border-subtle p-2 text-2xs font-bold text-text-tertiary uppercase tracking-wider"
                style={{ gridColumn: `1 / -1` }}
              >
                Inventar
              </div>
            )}
            {inventoryRows.map((item) => (
              <GanttRow
                key={`i-${item.id}`}
                label={item.name}
                days={days}
                entries={(inventoryEntries[item.id] ?? []).filter(
                  (e) => !filterProject || e.projectId === filterProject,
                )}
                dragState={dragState}
                onBarPointerDown={onBarPointerDown}
              />
            ))}
          </div>
        </div>
      )}

      {/* Create panel */}
      {createOpen && (
        <DispositionFormPanel
          users={users}
          projects={projects}
          inventoryItems={inventoryItems}
          onClose={() => setCreateOpen(false)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['disposition'] })
            setCreateOpen(false)
          }}
        />
      )}

      {/* Edit panel */}
      {editEntry && (
        <DispositionFormPanel
          entry={editEntry}
          users={users}
          projects={projects}
          inventoryItems={inventoryItems}
          onClose={() => setEditEntry(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['disposition'] })
            setEditEntry(null)
          }}
          onDelete={() => {
            deleteMut.mutate(editEntry.id)
            setEditEntry(null)
          }}
        />
      )}
    </section>
  )
}

// ── Gantt Row ──────────────────────────────────────────────────────────

function GanttRow({
  label,
  days,
  entries,
  dragState,
  onBarPointerDown,
}: {
  label: string
  days: Date[]
  entries: DispositionEntry[]
  dragState: GanttDragState | null
  onBarPointerDown: (e: ReactPointerEvent, entry: DispositionEntry, dayIndex: number) => void
}) {
  const dayMs = 86400000

  return (
    <>
      <div className="sticky left-0 z-10 bg-bg-elevated border-b border-r border-border-subtle p-2 text-xs text-text-primary font-medium truncate">
        {label}
      </div>
      {days.map((d, dayIndex) => {
        const dayStart = d.getTime()
        const dayEnd = dayStart + dayMs

        // Find entries that overlap this day
        const overlapping = entries.filter((e) => {
          const eStart = new Date(e.startTime).getTime()
          const eEnd = new Date(e.endTime).getTime()
          return eStart < dayEnd && eEnd > dayStart
        })

        // Check if a ghost should appear here: entry being dragged that would
        // land on this day after applying daysDelta
        const ghostEntries: DispositionEntry[] = []
        if (dragState) {
          const draggedEntry = entries.find((e) => e.id === dragState.entryId)
          if (draggedEntry && dragState.daysDelta !== 0) {
            const shifted = shiftEntryDays(draggedEntry, dragState.daysDelta)
            const sStart = new Date(shifted.startTime).getTime()
            const sEnd = new Date(shifted.endTime).getTime()
            if (sStart < dayEnd && sEnd > dayStart) {
              ghostEntries.push(shifted)
            }
          }
        }

        // Check for conflicts (2+ entries on same day, excluding dragged entry's original)
        const visibleEntries = overlapping.filter(
          (e) => !(dragState && e.id === dragState.entryId && dragState.daysDelta !== 0),
        )
        const hasConflict = visibleEntries.length + ghostEntries.length > 1

        return (
          <div
            key={d.toISOString()}
            className={cn(
              'border-b border-border-subtle/50 relative min-h-[32px]',
              hasConflict && 'bg-red-500/5',
              (d.getDay() === 0 || d.getDay() === 6) && 'bg-bg-overlay/20',
            )}
          >
            {visibleEntries.map((e, idx) => {
              const eStart = new Date(e.startTime).getTime()
              const eEnd = new Date(e.endTime).getTime()
              const isFirstDay = eStart >= dayStart && eStart < dayEnd
              const isLastDay = eEnd > dayStart && eEnd <= dayEnd
              const isDragging = dragState?.entryId === e.id

              return (
                <div
                  key={e.id}
                  onPointerDown={(ev) => onBarPointerDown(ev, e, dayIndex)}
                  className={cn(
                    'absolute inset-x-0 h-6 flex items-center px-1 text-2xs font-medium text-white truncate touch:opacity-100 select-none',
                    isFirstDay && 'rounded-l ml-0.5',
                    isLastDay && 'rounded-r mr-0.5',
                    !isFirstDay && !isLastDay && 'mx-0',
                    hasConflict && 'ring-1 ring-red-500/40',
                    isDragging ? 'opacity-30 cursor-grabbing' : 'cursor-grab',
                  )}
                  style={{
                    backgroundColor: e.color ?? '#3B82F6',
                    top: `${idx * 7 + 3}px`,
                  }}
                  title={`${e.title}${e.projectName ? ` (${e.projectName})` : ''}`}
                >
                  {isFirstDay ? e.title : ''}
                </div>
              )
            })}

            {/* Ghost preview for dragged entry at its new position */}
            {ghostEntries.map((ge) => {
              const geStart = new Date(ge.startTime).getTime()
              const geEnd = new Date(ge.endTime).getTime()
              const isFirstDay = geStart >= dayStart && geStart < dayEnd
              const isLastDay = geEnd > dayStart && geEnd <= dayEnd
              const stackIdx = visibleEntries.length

              return (
                <div
                  key={`ghost-${ge.id}`}
                  className={cn(
                    'absolute inset-x-0 h-6 flex items-center px-1 text-2xs font-medium text-white truncate pointer-events-none border border-dashed border-white/50',
                    isFirstDay && 'rounded-l ml-0.5',
                    isLastDay && 'rounded-r mr-0.5',
                    !isFirstDay && !isLastDay && 'mx-0',
                  )}
                  style={{
                    backgroundColor: ge.color ?? '#3B82F6',
                    top: `${stackIdx * 7 + 3}px`,
                  }}
                >
                  {isFirstDay ? ge.title : ''}
                </div>
              )
            })}

            {hasConflict && (
              <AlertTriangle className="absolute bottom-0.5 right-0.5 h-2.5 w-2.5 text-red-400" />
            )}
          </div>
        )
      })}
    </>
  )
}

/** Create a shallow copy of an entry with startTime/endTime shifted by N days */
function shiftEntryDays(entry: DispositionEntry, daysDelta: number): DispositionEntry {
  const newStart = new Date(entry.startTime)
  newStart.setDate(newStart.getDate() + daysDelta)
  const newEnd = new Date(entry.endTime)
  newEnd.setDate(newEnd.getDate() + daysDelta)
  return {
    ...entry,
    startTime: newStart.toISOString(),
    endTime: newEnd.toISOString(),
  }
}

// ── Disposition Form Panel ──────────────────────────────────────────────

function DispositionFormPanel({
  entry,
  users,
  projects,
  inventoryItems,
  onClose,
  onSaved,
  onDelete,
}: {
  entry?: DispositionEntry
  users: Array<{ id: string; name: string; email: string }>
  projects: Array<{ id: string; name: string; color?: string | null }>
  inventoryItems: Array<{ id: string; name: string }>
  onClose: () => void
  onSaved: () => void
  onDelete?: () => void
}) {
  const [title, setTitle] = useState(entry?.title ?? '')
  const [projectId, setProjectId] = useState(entry?.projectId ?? '')
  const [resourceType, setResourceType] = useState<'user' | 'inventory'>(
    entry?.inventoryItemId ? 'inventory' : 'user',
  )
  const [userId, setUserId] = useState(entry?.userId ?? '')
  const [inventoryItemId, setInventoryItemId] = useState(entry?.inventoryItemId ?? '')
  const [startTime, setStartTime] = useState(
    entry ? entry.startTime.slice(0, 16) : new Date().toISOString().slice(0, 16),
  )
  const [endTime, setEndTime] = useState(
    entry ? entry.endTime.slice(0, 16) : new Date(Date.now() + 3600000).toISOString().slice(0, 16),
  )
  const [location, setLocation] = useState(entry?.location ?? '')
  const [notes, setNotes] = useState(entry?.notes ?? '')
  const [color, setColor] = useState(entry?.color ?? '#3B82F6')

  // Conflict check
  const { data: conflictData } = useQuery({
    queryKey: [
      'disposition',
      'conflicts',
      resourceType,
      userId,
      inventoryItemId,
      startTime,
      endTime,
      entry?.id,
    ],
    queryFn: () =>
      api.checkDispositionConflicts({
        userId: resourceType === 'user' && userId ? userId : undefined,
        inventoryItemId:
          resourceType === 'inventory' && inventoryItemId ? inventoryItemId : undefined,
        startTime: new Date(startTime).toISOString(),
        endTime: new Date(endTime).toISOString(),
        excludeId: entry?.id,
      }),
    enabled:
      !!(
        (resourceType === 'user' && userId) ||
        (resourceType === 'inventory' && inventoryItemId)
      ) &&
      !!startTime &&
      !!endTime,
  })

  const hasConflicts = conflictData?.hasConflicts ?? false

  const saveMut = useMutation({
    mutationFn: () => {
      const data = {
        title,
        projectId: projectId || null,
        userId: resourceType === 'user' && userId ? userId : null,
        inventoryItemId: resourceType === 'inventory' && inventoryItemId ? inventoryItemId : null,
        startTime: new Date(startTime).toISOString(),
        endTime: new Date(endTime).toISOString(),
        location: location || null,
        notes: notes || null,
        color,
      }
      if (entry) {
        return api.updateDisposition(entry.id, data)
      }
      return api.createDisposition(data)
    },
    onSuccess: () => {
      notify.success(entry ? 'Gespeichert' : 'Eintrag erstellt')
      onSaved()
    },
    onError: (err: Error) => notify.error(err.message),
  })

  return (
    <SidePanel
      open
      onClose={onClose}
      title={entry ? 'Eintrag bearbeiten' : 'Neuer Eintrag'}
      footer={
        <div className="flex items-center gap-2">
          {entry && onDelete && (
            <Button
              variant="ghost"
              className="text-red-400 hover:text-red-300"
              onClick={() => {
                if (confirm('Eintrag löschen?')) onDelete()
              }}
            >
              <Trash2 className="h-4 w-4" /> Löschen
            </Button>
          )}
          <span className="flex-1" />
          <Button variant="ghost" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            disabled={!title.trim() || !startTime || !endTime || saveMut.isPending}
            onClick={() => saveMut.mutate()}
          >
            {entry ? 'Speichern' : 'Erstellen'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {hasConflicts && (
          <div className="rounded-lg border border-red-500/30 bg-red-500/5 p-3 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-red-400 shrink-0 mt-0.5" />
            <div className="text-xs text-red-300">
              <p className="font-medium">Konflikt erkannt</p>
              <p>Die gewählte Ressource ist in diesem Zeitraum bereits gebucht.</p>
            </div>
          </div>
        )}

        <div className="space-y-2">
          <Label>Titel</Label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="z.B. Montage Serverraum"
            autoFocus
          />
        </div>

        <div className="space-y-2">
          <Label>Projekt</Label>
          <Select value={projectId} onChange={(e) => setProjectId(e.target.value)}>
            <option value="">Kein Projekt</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </Select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label>Start</Label>
            <Input
              type="datetime-local"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Ende</Label>
            <Input
              type="datetime-local"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
            />
          </div>
        </div>

        {/* Resource type toggle */}
        <div className="space-y-2">
          <Label>Ressourcentyp</Label>
          <div className="inline-flex rounded-md border border-border-default bg-bg-overlay/40 p-0.5 w-full">
            <button
              type="button"
              onClick={() => setResourceType('user')}
              className={cn(
                'flex-1 rounded px-3 py-1.5 text-xs transition-colors',
                resourceType === 'user'
                  ? 'bg-accent/15 text-accent'
                  : 'text-text-secondary hover:text-text-primary',
              )}
            >
              Mitarbeiter
            </button>
            <button
              type="button"
              onClick={() => setResourceType('inventory')}
              className={cn(
                'flex-1 rounded px-3 py-1.5 text-xs transition-colors',
                resourceType === 'inventory'
                  ? 'bg-accent/15 text-accent'
                  : 'text-text-secondary hover:text-text-primary',
              )}
            >
              Inventar
            </button>
          </div>
        </div>

        {resourceType === 'user' ? (
          <div className="space-y-2">
            <Label>Mitarbeiter</Label>
            <Select value={userId} onChange={(e) => setUserId(e.target.value)}>
              <option value="">Nicht zugewiesen</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </Select>
          </div>
        ) : (
          <div className="space-y-2">
            <Label>Inventar-Objekt</Label>
            <Select value={inventoryItemId} onChange={(e) => setInventoryItemId(e.target.value)}>
              <option value="">Nicht zugewiesen</option>
              {inventoryItems.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.name}
                </option>
              ))}
            </Select>
          </div>
        )}

        <div className="space-y-2">
          <Label>Ort</Label>
          <Input
            value={location}
            onChange={(e) => setLocation(e.target.value)}
            placeholder="z.B. Kundenstandort München"
          />
        </div>

        <div className="space-y-2">
          <Label>Notizen</Label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            className="w-full rounded-lg border border-border-default bg-bg-overlay/50 px-3 py-2 text-base lg:text-sm text-text-primary placeholder:text-text-tertiary focus-visible:outline-none focus-visible:border-accent/60 focus-visible:ring-2 focus-visible:ring-accent/20 resize-none"
            placeholder="Optionale Notizen"
          />
        </div>

        <div className="flex items-center gap-2">
          <Label>Farbe</Label>
          <input
            type="color"
            value={color}
            onChange={(e) => setColor(e.target.value)}
            className="h-7 w-7 rounded border border-border-default cursor-pointer"
          />
          <span className="text-xs text-text-tertiary">{color}</span>
        </div>
      </div>
    </SidePanel>
  )
}
